// The sign-in page's circuit-board background — the network it draws.
//
// Nodes on a grid (none in a clear circle round the card), straight traces
// between grid neighbours, and a few "data" particles running along traces.
// Home.jsx's CircuitBackground draws it on a canvas and rebuilds it on every
// window resize.
//
// A window can be tiny — a phone browser being minimised, a split screen, a
// pane that is momentarily 0 px wide — and then the clear circle swallows
// every node: NO traces. A particle must never point at a trace that is not
// there: that threw inside the animation frame ("reading 'ax'"), the next
// frame was never scheduled, and the background stayed blank even after the
// window grew back (until a reload). So: no traces → no particles, and a
// particle whose trace is gone is skipped, not drawn.
//
// Pure — no imports, no DOM — so the static suite proves it
// (auth/test/run-circuit-network-tests.mjs). `random` is injectable for that.

export const SPACING = 55;
export const CLEAR_RADIUS = 170;

export function generateNetwork(w, h, random = Math.random) {
  const nodes = [];
  const cols = Math.ceil(w / SPACING) + 1;
  const rows = Math.ceil(h / SPACING) + 1;
  const cx = w / 2;
  const cy = h / 2;

  // Build a grid lookup for strictly orthogonal connections
  const grid = {};
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      if (random() > 0.65) continue;
      const bx = c * SPACING;
      const by = r * SPACING;
      const dx = bx - cx;
      const dy = by - cy;
      const distCenter = Math.sqrt(dx * dx + dy * dy);
      if (distCenter < CLEAR_RADIUS) continue;
      if (distCenter < CLEAR_RADIUS + 70 && random() > 0.35) continue;
      const node = {
        x: bx,
        y: by,
        col: c,
        row: r,
        r: 2 + random() * 2,
        phase: random() * Math.PI * 2,
        ring: random() < 0.2,
      };
      nodes.push(node);
      grid[`${c},${r}`] = node;
    }
  }

  // Create strictly orthogonal traces (horizontal or vertical only)
  const traces = [];
  const traceSet = new Set();
  for (const node of nodes) {
    const { col, row } = node;
    // Look for neighbors in 4 cardinal directions, up to 3 cells away
    const directions = [
      { dc: 1, dr: 0 },  // right
      { dc: 0, dr: 1 },  // down
      { dc: -1, dr: 0 }, // left
      { dc: 0, dr: -1 }, // up
    ];
    let connections = 0;
    // Shuffle directions for variety
    for (let i = directions.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [directions[i], directions[j]] = [directions[j], directions[i]];
    }
    for (const { dc, dr } of directions) {
      if (connections >= 2) break;
      for (let step = 1; step <= 3; step++) {
        const nc = col + dc * step;
        const nr = row + dr * step;
        const neighbor = grid[`${nc},${nr}`];
        if (neighbor) {
          const key = [
            Math.min(node.x, neighbor.x), Math.min(node.y, neighbor.y),
            Math.max(node.x, neighbor.x), Math.max(node.y, neighbor.y),
          ].join(',');
          if (!traceSet.has(key)) {
            traceSet.add(key);
            // Pure horizontal or vertical line — no bend needed
            traces.push({
              ax: node.x, ay: node.y,
              bx: neighbor.x, by: neighbor.y,
              horizontal: dr === 0,
            });
            connections++;
          }
          break; // Don't look further in this direction
        }
      }
    }
  }

  // Data-flow particles — only where there is a trace to run along.
  const particles = [];
  const particleCount = traces.length ? Math.min(14, Math.max(10, Math.floor(traces.length * 0.05))) : 0;
  for (let i = 0; i < particleCount; i++) {
    particles.push({
      traceIdx: Math.floor(random() * traces.length),
      t: random(),
      speed: 0.0008 + random() * 0.0012,
    });
  }

  return { nodes, traces, particles };
}

// Moves one particle a frame along its trace (to a fresh trace at the end)
// and answers where to draw it — or null when there is no trace to draw on.
export function stepParticle(p, traces, random = Math.random) {
  if (!traces.length) return null;
  p.t += p.speed;
  if (p.t > 1) {
    p.t = 0;
    p.traceIdx = Math.floor(random() * traces.length);
  }
  const tr = traces[p.traceIdx];
  if (!tr) return null;
  // Interpolate along straight trace (a -> b)
  return { x: tr.ax + (tr.bx - tr.ax) * p.t, y: tr.ay + (tr.by - tr.ay) * p.t };
}
